import { memo, useRef } from "react";
import clsx from "clsx";

import mainClasses from "../styles.module.scss";
import { FormInputBox, FormInputIcon } from "../formControl";

import type { ISelectProps } from "./types";

import { useReadOnly } from "@/hooks";
import {
  InputMenuItemPopover,
  InputPopover,
  InputPopoverContent,
  useInputPopoverContext,
} from "@/components/organisms/inputPopover";

export const Select = memo(function Select(props: ISelectProps) {
  const { disabled: propsDisabled, readOnly: propsReadOnly, ...rest } = props;
  const readOnly = useReadOnly(propsReadOnly);
  const disabled = propsDisabled || rest.isLoading;

  return (
    <InputPopover ariaRole={"select"} disabled={disabled || readOnly}>
      <SelectInput {...rest} disabled={disabled} readOnly={readOnly} />
    </InputPopover>
  );
});

function SelectInput(props: ISelectProps) {
  const {
    readOnly,
    disabled,
    value = { key: "", value: "" },
    onChange,
    onBlur,
    onFocus,
    onMouseDown,
    options,
    className,
    validation,
    isLoading,
    isError,
    ...rest
  } = props;

  const {
    activeIndex,
    setActiveIndex,
    setOpen,
    refs,
    open: isOpen,
    getReferenceProps,
  } = useInputPopoverContext();
  const inputRef = useRef<HTMLInputElement>(null);

  const focusOnInput = () => inputRef.current?.focus();

  const handleSelect = () => {
    if (activeIndex !== null) {
      const activeItem = options[activeIndex];

      onChange?.(activeItem);
      setOpen(false);
    }
  };

  return (
    <>
      <FormInputBox
        ref={refs.setReference}
        disabled={!!disabled}
        readOnly={!!readOnly}
        validation={validation}
        onMouseDown={(event) => {
          event.preventDefault();
          focusOnInput();
          setOpen(!isOpen);
        }}
        style={{ cursor: "default" }}
      >
        <div className={mainClasses.inputContainer}>
          <input
            {...getReferenceProps({
              style: { cursor: "default" },
              "aria-autocomplete": "list",
              onKeyDown(event) {
                switch (event.key) {
                  case "Enter":
                    // prevent form submitting
                    event.preventDefault();
                    handleSelect();
                    break;

                  case "Tab":
                    setOpen(false);
                    break;
                  default:
                    if (event.key.length === 1 && !open) setOpen(true);
                    break;
                }
              },
            })}
            className={clsx([mainClasses.input, "w-100"])}
            readOnly
            disabled={disabled}
            ref={inputRef}
            value={value.value}
            autoComplete={"off"}
            onFocus={(event) => {
              setOpen(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              // setOpen(false);
              onBlur?.(event);
            }}
            onMouseDown={(event) => {
              event.stopPropagation();
              // to disable input value selection
              event.preventDefault();
              focusOnInput();
              setOpen(!isOpen);
              onMouseDown?.(event);
            }}
            {...rest}
          />
        </div>
        <FormInputIcon
          iconName={"AltArrowDown"}
          type={"bgIcon"}
          onMouseDown={(event) => {
            // prevent to focus out input
            event.preventDefault();
            // prevent to call `FormControlBox` onMouseDownEvent
            event.stopPropagation();
          }}
          {...{ isLoading, isError, disabled, readOnly }}
        />
      </FormInputBox>
      <InputPopoverContent>
        {options.map((item, index) => {
          return (
            <InputMenuItemPopover
              key={item.key}
              index={index}
              onClick={handleSelect}
              selected={item.key === value.key}
              onMouseDown={
                // cause we don't want to lose input's focus on icon clicking event
                (event) => {
                  event.preventDefault();
                  setActiveIndex(index);
                }
              }
            >
              {item.value}
            </InputMenuItemPopover>
          );
        })}
      </InputPopoverContent>
    </>
  );
}
